import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Text,
  Tooltip,
  useColorModeValue as mode,
  useDisclosure,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaEye, FaPlus, FaTrash } from 'react-icons/fa';

import AddCompanyModal from './AddCompanyModal';
import CompanyDetailModal from './CompanyDetailModal';
import DeleteCompanyModal from './DeleteCompanyModal';
import EditCompanyModal from './EditCompanyModal';
import AmatriumTableV2 from '../../components/AmatriumTableV2';
import Paginate from '../../components/Paginate';
import {
  useCreateOrganization,
  useDeleteOrganization,
  useOrganizations,
  useUpdateOrganizationLimitations,
} from '../../queries/useOrganization';
import {
  CreateOrganizationPayload,
  Organization,
  OrganizationLimitation,
} from '../../services/organization';

const ManagementSystem = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Utility function to format numbers
  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';

    // If it's a decimal number with more than 2 decimal places, round to 2
    if (value % 1 !== 0) {
      const rounded = Math.round(value * 100) / 100;
      return rounded.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    // If it's a whole number, add thousand separators
    return value.toLocaleString('en-US');
  };

  const {
    isOpen: isAddCompanyModalOpen,
    onOpen: onOpenAddCompanyModal,
    onClose: onCloseAddCompanyModal,
  } = useDisclosure();

  const {
    isOpen: isViewCompanyModalOpen,
    onOpen: onOpenViewCompanyModal,
    onClose: onCloseViewCompanyModal,
  } = useDisclosure();

  const {
    isOpen: isDeleteCompanyModalOpen,
    onOpen: onOpenDeleteCompanyModal,
    onClose: onCloseDeleteCompanyModal,
  } = useDisclosure();

  const {
    isOpen: isEditCompanyModalOpen,
    onOpen: onOpenEditCompanyModal,
    onClose: onCloseEditCompanyModal,
  } = useDisclosure();
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companyToDelete, setCompanyToDelete] = useState<Organization | null>(null);
  const [companyToEdit, setCompanyToEdit] = useState<Organization | null>(null);
  const [pageSize] = useState(10);

  // Check if any modal is open
  const isAnyModalOpen =
    isAddCompanyModalOpen ||
    isViewCompanyModalOpen ||
    isDeleteCompanyModalOpen ||
    isEditCompanyModalOpen;

  // Force close all tooltips when modal closes
  useEffect(() => {
    if (!isAnyModalOpen) {
      // Force close any open tooltips by dispatching a click event
      const tooltips = document.querySelectorAll('[data-tooltip]');
      tooltips.forEach((tooltip) => {
        const event = new MouseEvent('mouseleave', {
          bubbles: true,
          cancelable: true,
        });
        tooltip.dispatchEvent(event);
      });
    }
  }, [isAnyModalOpen]);

  // React Query hooks
  const { data: companyData, isFetching } = useOrganizations(
    {
      page: currentPage,
      size: pageSize,
      name: searchTerm || undefined,
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 0, // Data sẽ được coi là stale ngay lập tức
    } as any
  );

  const createOrganizationMutation = useCreateOrganization();
  const deleteOrganizationMutation = useDeleteOrganization();
  const updateLimitationsMutation = useUpdateOrganizationLimitations();

  // Color mode values
  const bgContainer = mode('bg_main_container', 'bg_main_container');
  const colorMainText = mode('color_main_text', 'color_main_text');
  const colorSecondaryText = mode('color_main_text', 'color_main_text');

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search function with useRef to persist timeout
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  const debouncedSearch = useCallback((_searchValue: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(0); // Reset to first page when searching
      // React Query sẽ tự động refetch khi searchTerm thay đổi
    }, 500); // 500ms delay
  }, []);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearch = () => {
    setCurrentPage(0); // Reset to first page when searching
    // React Query sẽ tự động refetch khi searchTerm thay đổi
  };

  const handleAddCompany = () => {
    onOpenAddCompanyModal();
  };

  const handleSubmitCompany = (companyData: CreateOrganizationPayload) => {
    createOrganizationMutation.mutate(companyData, {
      onSuccess: (data: any) => {
        if (!data?.success && data?.message) {
          return;
        }
        // Close modal when success
        onCloseAddCompanyModal();
        // Show success message from API - API returns simple string message
        const successMessage = typeof data === 'string' ? data : data?.message;
        toast({
          title: t('managementSystem.toast.success'),
          description: successMessage,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      },
      onError: (error: any) => {
        // Keep modal open when error and show error toast with API message
        console.error('Failed to create organization:', error);
      },
    });
  };

  const handleEditCompany = (company: Organization) => {
    setCompanyToEdit(company);
    onOpenEditCompanyModal();
  };

  const handleViewUsage = (company: Organization) => {
    // Pass the original company data with id for API calls
    setSelectedCompany(company);
    onOpenViewCompanyModal();
  };

  const handleDeleteCompany = (company: Organization) => {
    setCompanyToDelete(company);
    onOpenDeleteCompanyModal();
  };

  const handleConfirmDelete = (organization: Organization) => {
    deleteOrganizationMutation.mutate(organization.id, {
      onSuccess: (data: any) => {
        if (!data?.success && data?.message) {
          return;
        }
        // Show success message from API
        const successMessage =
          typeof data === 'string'
            ? data
            : data?.message || t('managementSystem.toast.organizationDeleted');
        toast({
          title: t('managementSystem.toast.success'),
          description: successMessage,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      },
      onError: (error: any) => {
        console.error('Error deleting organization:', error);
      },
    });
  };

  const handleEditCompanyLimitations = (limitations: Partial<OrganizationLimitation>) => {
    if (!companyToEdit) return;

    updateLimitationsMutation.mutate(
      {
        id: companyToEdit.id,
        limitations,
      },
      {
        onSuccess: (data: any) => {
          if (!data?.success && data?.message) {
            return;
          }
          // Show success message from API
          const successMessage =
            typeof data === 'string'
              ? data
              : data?.message || t('managementSystem.toast.organizationUpdated');
          toast({
            title: t('managementSystem.toast.success'),
            description: successMessage,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        },
        onError: (error: any) => {
          console.error('Error updating organization limitations:', error);
        },
      }
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const columns = [
    {
      title: t('managementSystem.companyName'),
      width: { base: '150px', md: '300px' },
      dataIndex: 'name',
      key: 'name',
      render: (row: Organization) => (
        <Text color={colorMainText} fontSize={{ base: 'xs', md: 'sm' }} fontWeight='600'>
          {(row as any).isBlank ? '' : row.name}
        </Text>
      ),
    },
    {
      title: t('managementSystem.noAccount'),
      width: { base: '80px', md: '120px' },
      dataIndex: 'user_count',
      key: 'user_count',
      render: (row: Organization) => (
        <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}>
          {(row as any).isBlank ? '' : row.user_count}
        </Text>
      ),
    },
    {
      title: t('managementSystem.storageUsageLimit'),
      width: { base: '80px', md: '150px' },
      dataIndex: 'storage_gb',
      key: 'storage_gb',
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.storage_gb === null || row.limitation.storage_gb === undefined ? (
            <Badge fontSize={{ base: 'xs', md: 'sm' }} colorScheme='green' variant='subtle'>
              {t('managementSystem.unlimited')}
            </Badge>
          ) : (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}>
              {formatNumber(row.current_usage.total_storage_gb)}/
              {formatNumber(row.limitation.storage_gb || 0)}
            </Text>
          )}
        </>
      ),
    },
    {
      title: t('managementSystem.questionUsageLimit'),
      width: { base: '80px', md: '120px' },
      dataIndex: 'question',
      key: 'question',
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.question === null || row.limitation.question === undefined ? (
            <Badge fontSize={{ base: 'xs', md: 'sm' }} colorScheme='green' variant='subtle'>
              {t('managementSystem.unlimited')}
            </Badge>
          ) : (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}>
              {formatNumber(row.current_usage.total_questions)}/
              {formatNumber(row.limitation.question)}
            </Text>
          )}
        </>
      ),
    },
    {
      title: t('managementSystem.translationUsageLimit'),
      width: { base: '80px', md: '150px' },
      dataIndex: 'translation',
      key: 'translation',
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.translation === null || row.limitation.translation === undefined ? (
            <Badge fontSize={{ base: 'xs', md: 'sm' }} colorScheme='green' variant='subtle'>
              {t('managementSystem.unlimited')}
            </Badge>
          ) : (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}>
              {formatNumber(row.current_usage.total_translations)}/
              {formatNumber(row.limitation.translation)}
            </Text>
          )}
        </>
      ),
    },
    {
      title: t('managementSystem.expirationDate'),
      width: { base: '80px', md: '120px' },
      dataIndex: 'expiration',
      key: 'expiration',
      isShowTooltip: false,
      render: (row: Organization) => (
        <>
          {(row as any).isBlank ? (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}></Text>
          ) : row.limitation.expiration === 'Unlimited' ||
            row.limitation.expiration === null ||
            row.limitation.expiration === undefined ? (
            <Badge fontSize={{ base: 'xs', md: 'sm' }} colorScheme='green' variant='subtle'>
              {t('managementSystem.unlimited')}
            </Badge>
          ) : (
            <Text color={colorSecondaryText} fontSize={{ base: 'xs', md: 'sm' }}>
              {new Date(row.limitation.expiration).toISOString().split('T')[0]}
            </Text>
          )}
        </>
      ),
    },
    {
      key: 'actions',
      title: t('managementSystem.actions'),
      width: { base: '100px', md: '120px' },
      notShowTooltip: true,
      render: (row: Organization) => (
        <Flex gap={{ base: 1, md: 2 }}>
          {(row as any).isBlank ? (
            // Show empty space for blank rows
            <Box w='100%' h='32px' />
          ) : (
            <>
              {isAnyModalOpen ? (
                <IconButton
                  color='blue.500'
                  aria-label='Edit company'
                  icon={<FaEdit />}
                  onClick={() => handleEditCompany(row)}
                  size={{ base: 'xs', md: 'sm' }}
                  variant='ghost'
                />
              ) : (
                <Tooltip label={t('managementSystem.tooltipEditCompany')}>
                  <IconButton
                    color='blue.500'
                    aria-label='Edit company'
                    icon={<FaEdit />}
                    onClick={() => handleEditCompany(row)}
                    size={{ base: 'xs', md: 'sm' }}
                    variant='ghost'
                  />
                </Tooltip>
              )}

              {isAnyModalOpen ? (
                <IconButton
                  color='green.500'
                  aria-label='View company usage'
                  icon={<FaEye />}
                  onClick={() => handleViewUsage(row)}
                  size={{ base: 'xs', md: 'sm' }}
                  variant='ghost'
                />
              ) : (
                <Tooltip label={t('managementSystem.viewUsage')}>
                  <IconButton
                    color='green.500'
                    aria-label='View company usage'
                    icon={<FaEye />}
                    onClick={() => handleViewUsage(row)}
                    size={{ base: 'xs', md: 'sm' }}
                    variant='ghost'
                  />
                </Tooltip>
              )}

              {isAnyModalOpen ? (
                <IconButton
                  color='red.500'
                  aria-label='Delete company'
                  icon={<FaTrash />}
                  onClick={() => handleDeleteCompany(row)}
                  size={{ base: 'xs', md: 'sm' }}
                  variant='ghost'
                />
              ) : (
                <Tooltip label={t('managementSystem.tooltipDeleteCompany')}>
                  <IconButton
                    color='red.500'
                    aria-label='Delete company'
                    icon={<FaTrash />}
                    onClick={() => handleDeleteCompany(row)}
                    size={{ base: 'xs', md: 'sm' }}
                    variant='ghost'
                  />
                </Tooltip>
              )}
            </>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <Box h={'calc(100vh - 150px)'} bg={bgContainer}>
      <Heading as='h3' mr='1rem' fontWeight={'bold'} size='md'>
        {t('managementSystem.title')}
      </Heading>

      {/* Search and Add Company Section */}
      <Flex
        align='center'
        justify='space-between'
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mt='5'
        mb='5'
      >
        <Flex gap={3} w={{ base: '100%', md: 'auto' }}>
          <Input
            w={{ base: '100%', md: '300px' }}
            onChange={handleSearchInputChange}
            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
              e.key === 'Enter' && handleSearch()
            }
            placeholder={t('managementSystem.searchPlaceholder')}
            value={searchTerm}
          />
          <Button
            h='39'
            borderRadius='4px'
            onClick={handleSearch}
            type='submit'
            variant='amatrium_submit'
          >
            {t('managementSystem.search')}
          </Button>
        </Flex>

        <Button
          h='39'
          borderRadius='4px'
          leftIcon={<FaPlus />}
          onClick={handleAddCompany}
          variant='amatrium_submit'
        >
          {t('managementSystem.addNewCompany')}
        </Button>
      </Flex>

      {/* Company Table */}
      <Box overflow='hidden' border={mode('0.5px solid rgb(209, 213, 219)', '')} borderRadius='md'>
        <Box overflowX={'auto'}>
          <AmatriumTableV2
            columns={columns}
            data={companyData?.data || []}
            isLoading={isFetching}
            rowKey='id'
          />
        </Box>
        <Paginate
          total={companyData?.total_item || 0}
          current={currentPage + 1}
          pageSize={pageSize}
          onChange={(page: number) => handlePageChange(page - 1)}
          disabled={isFetching}
        />
      </Box>

      {/* Add Company Modal */}
      <AddCompanyModal
        isOpen={isAddCompanyModalOpen}
        onClose={onCloseAddCompanyModal}
        onSubmit={handleSubmitCompany}
      />

      {/* Company Detail Modal */}
      <CompanyDetailModal
        isOpen={isViewCompanyModalOpen}
        onClose={onCloseViewCompanyModal}
        companyData={selectedCompany}
      />

      {/* Delete Company Modal */}
      <DeleteCompanyModal
        isOpen={isDeleteCompanyModalOpen}
        onClose={() => {
          onCloseDeleteCompanyModal();
          setCompanyToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        organization={companyToDelete}
      />

      <EditCompanyModal
        isOpen={isEditCompanyModalOpen}
        onClose={() => {
          onCloseEditCompanyModal();
          setCompanyToEdit(null);
        }}
        organization={companyToEdit}
        onSubmit={handleEditCompanyLimitations}
      />
    </Box>
  );
};

export default ManagementSystem;
